// Resolve free-text crown / crystal prose to lookup slugs.
//
// The crown/crystal multi-select split has to clean up what the scalar era
// left behind: an extractor's whole marketing phrase, comma-joined, in one
// unvalidated custom value.
//
//   crownType: "Screw-down, Twinlock double waterproofness system"
//   crystal:   "Scratch-resistant sapphire, Cyclops lens over the date"
//
// Split on the comma and each part is *about* one lookup value, but is almost
// never spelled like one. This module answers "which slug is this part?" for
// the migrateCrownTypeCrystal sweep, and answers "I don't know" rather than
// guessing — an unresolved part is reported for manual triage, never mapped to
// a plausible-looking neighbour.
//
// NOTE: hand-mirrored inside functions/src/migrateCrownTypeCrystal.ts, which
// has `rootDir: "src"` and cannot import across the package boundary (the same
// arrangement as functions/src/calibreKey.ts ↔ src/services/calibreResolution.ts
// and the two copies of enrichEnumFields). Changes here MUST be mirrored there;
// this copy is the one with tests.

/** The subset of a lookup value the resolver reads. Structurally compatible
 *  with both the client `LookupValue` and the functions-side `LookupValueLite`. */
export interface ResolveCandidate {
  value?: string;
  displayName?: string;
  synonyms?: string[];
}

export type ResolveMatchKind =
  | 'value'
  | 'synonym'
  | 'display'
  | 'contains'
  | 'none';

export interface ResolveResult {
  /** The canonical slug, or null when nothing matched confidently. */
  slug: string | null;
  kind: ResolveMatchKind;
  /** Set when two DIFFERENT slugs tied on the containment pass. */
  ambiguousWith?: string[];
}

/**
 * Comparison key: lowercase, and every run of non-alphanumerics collapsed to a
 * single space. Folds the three spellings of the same value that actually occur
 * — slug `screw_down`, displayName "Screw-down crown", scrape text "Screw down"
 * — onto one string.
 */
export function normalizeForMatch(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Generic nouns a lookup displayName tacks on that the source prose usually
 * omits: "Sapphire Crystal" vs "Scratch-resistant sapphire", "Screw-down crown"
 * vs "Screw-down". Stripped to produce an extra comparison key, never to
 * replace the full one.
 */
const GENERIC_SUFFIXES = ['crystal', 'glass', 'crown', 'lens', 'bezel'];

/** Shortest key length allowed on the containment pass. Below this the key is
 *  a fragment ("k1", "d") that would match inside unrelated words. */
const MIN_CONTAINMENT_LEN = 4;

function stripGenericSuffix(normalized: string): string | null {
  for (const suffix of GENERIC_SUFFIXES) {
    if (normalized.endsWith(` ${suffix}`)) {
      const stripped = normalized.slice(0, -(suffix.length + 1)).trim();
      if (stripped) return stripped;
    }
  }
  return null;
}

/** Every normalized spelling of one candidate, for the containment pass. */
function containmentKeys(candidate: ResolveCandidate): string[] {
  const raw = [
    candidate.value,
    candidate.displayName,
    ...(candidate.synonyms ?? []),
  ].filter((s): s is string => typeof s === 'string' && s.trim() !== '');

  const keys = new Set<string>();
  for (const r of raw) {
    const n = normalizeForMatch(r);
    if (!n) continue;
    keys.add(n);
    const stripped = stripGenericSuffix(n);
    if (stripped) keys.add(stripped);
  }
  return [...keys];
}

/** Whole-word containment: `key` appears in `haystack` on both boundaries, so
 *  "flat" does not match inside "flattened". Both sides are already normalized
 *  to space-separated alphanumeric tokens, so a plain token-window compare is
 *  enough — and avoids building a RegExp per candidate per part. */
function containsWholeWords(haystack: string, key: string): boolean {
  if (!key) return false;
  const hay = haystack.split(' ');
  const needle = key.split(' ');
  if (needle.length > hay.length) return false;
  for (let i = 0; i + needle.length <= hay.length; i++) {
    let ok = true;
    for (let j = 0; j < needle.length; j++) {
      if (hay[i + j] !== needle[j]) {
        ok = false;
        break;
      }
    }
    if (ok) return true;
  }
  return false;
}

/**
 * Resolve one comma-split part to a lookup slug, in four passes of decreasing
 * confidence. Passes 1–3 are equality on the normalized key; pass 4 looks for a
 * candidate spelling INSIDE the part, which is what carries "Twinlock double
 * waterproofness system" → `twinlock` and "Scratch-resistant sapphire" →
 * `sapphire_crystal` (via the suffix-stripped key "sapphire").
 *
 * Pass 4 takes the LONGEST matching key, so "sapphire" wins over "glass" in
 * "Sapphire glass". Two DIFFERENT slugs tied at that length is a genuine
 * ambiguity and resolves to null with `ambiguousWith` set — the migration flags
 * it for a human rather than picking one.
 */
export function resolveToSlug(
  part: string,
  candidates: ResolveCandidate[],
): ResolveResult {
  const normalizedPart = normalizeForMatch(part);
  if (!normalizedPart) return { slug: null, kind: 'none' };

  const usable = candidates.filter(
    (c) => typeof c.value === 'string' && c.value.trim() !== '',
  );

  // Pass 1 — the slug itself.
  for (const c of usable) {
    if (normalizeForMatch(c.value!) === normalizedPart) {
      return { slug: c.value!.trim(), kind: 'value' };
    }
  }

  // Pass 2 — a declared synonym.
  for (const c of usable) {
    for (const syn of c.synonyms ?? []) {
      if (typeof syn === 'string' && normalizeForMatch(syn) === normalizedPart) {
        return { slug: c.value!.trim(), kind: 'synonym' };
      }
    }
  }

  // Pass 3 — the display name, with and without its generic noun.
  for (const c of usable) {
    if (typeof c.displayName !== 'string') continue;
    const n = normalizeForMatch(c.displayName);
    if (!n) continue;
    if (n === normalizedPart || stripGenericSuffix(n) === normalizedPart) {
      return { slug: c.value!.trim(), kind: 'display' };
    }
  }

  // Pass 4 — a candidate spelling contained in the part, longest key wins.
  let bestLen = 0;
  const bestSlugs = new Set<string>();
  for (const c of usable) {
    for (const key of containmentKeys(c)) {
      if (key.length < MIN_CONTAINMENT_LEN) continue;
      if (!containsWholeWords(normalizedPart, key)) continue;
      const slug = c.value!.trim();
      if (key.length > bestLen) {
        bestLen = key.length;
        bestSlugs.clear();
        bestSlugs.add(slug);
      } else if (key.length === bestLen) {
        bestSlugs.add(slug);
      }
    }
  }

  if (bestSlugs.size === 1) {
    return { slug: [...bestSlugs][0], kind: 'contains' };
  }
  if (bestSlugs.size > 1) {
    return {
      slug: null,
      kind: 'none',
      ambiguousWith: [...bestSlugs].sort(),
    };
  }

  return { slug: null, kind: 'none' };
}

export interface ResolveFieldOutcome {
  /** Wrapped + split parts exactly as stored, before any slug substitution. */
  parts: string[];
  /** Canonical slugs — populated ONLY when every part resolved. */
  slugs: string[];
  /** True when every part resolved and `slugs` is safe to write. */
  allResolved: boolean;
  /** The parts that did not resolve, verbatim, for the triage report. */
  unresolvedParts: string[];
}

/**
 * Resolve every part of one field's value.
 *
 * All-or-nothing on purpose: a half-canonicalized array ("sapphire_crystal" +
 * "Cyclops lens over the date") reads as curated data and would never get
 * looked at again, whereas leaving both parts raw keeps the ref visibly
 * unfinished and on the triage list.
 */
export function resolveFieldValues(
  parts: string[],
  candidates: ResolveCandidate[],
): ResolveFieldOutcome {
  const slugs: string[] = [];
  const unresolvedParts: string[] = [];

  for (const part of parts) {
    const res = resolveToSlug(part, candidates);
    if (res.slug) slugs.push(res.slug);
    else unresolvedParts.push(part);
  }

  const allResolved = unresolvedParts.length === 0 && parts.length > 0;

  // First-wins dedupe: two parts can legitimately resolve to the same slug
  // ("Sapphire" + "Scratch-resistant sapphire crystal").
  const seen = new Set<string>();
  const deduped: string[] = [];
  for (const s of slugs) {
    const key = s.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(s);
  }

  return {
    parts,
    slugs: allResolved ? deduped : [],
    allResolved,
    unresolvedParts,
  };
}
